import type { CallbackInterface } from "recoil";

import type { PageStateType } from "Types/state";

import { pageState } from "State/page";

import { logAppError } from "Helpers/logger";

import getIntl from "Helpers/intl";

import type { ModalBulkAddToGroupState } from "Pages/RecipientsList/model";

import {
	addToGroup,
	notifyBulkAddToGroup,
} from "Pages/RecipientsList/recoil/services/modalBulkAddToGroup";
import { addGroup } from "Pages/RecipientsList/recoil/services/modals";
import {
	appliedSearchFiltersState,
	searchGroupsInState,
} from "Pages/RecipientsList/recoil/state/filters";
import { modalBulkAddToGroupState } from "Pages/RecipientsList/recoil/state/modals";
import { selectionState } from "Pages/RecipientsList/recoil/state/results";
import { gainsightTrack } from "mailup-react-helpers";

export const modalBulkAddToGroupOpenCallback =
	({ set, snapshot }: CallbackInterface) =>
	async () => {
		gainsightTrack("mup_recipients_click_bulkActionAddGroup", {});
		const searchGroupsIn = await snapshot.getPromise(searchGroupsInState);

		set<ModalBulkAddToGroupState>(modalBulkAddToGroupState, (prev) => ({
			...prev,
			show: true,
			searchGroups: [...searchGroupsIn],
		}));
	};

export const modalBulkAddToGroupCancelCallback =
	({ reset }: CallbackInterface) =>
	async () => {
		reset(modalBulkAddToGroupState);
	};

export const modalBulkAddToGroupConfirmCallback =
	({ snapshot, set, reset }: CallbackInterface) =>
	async () => {
		set(pageState, (prev) => ({
			...prev,
			loading: true,
		}));

		const [
			{ mode, checked },
			searchFilters,
			{
				addToGroupId,
				optRemoveFromGroups,
				removeFromGroupsMode,
				optMoveRandom,
				moveRandomAmount,
				optSetAsTestGroup,
				searchGroups,
			},
		] = await Promise.all([
			snapshot.getPromise(selectionState),
			snapshot.getPromise(appliedSearchFiltersState),
			snapshot.getPromise(modalBulkAddToGroupState),
		]);

		try {
			await addToGroup(
				{
					mode,
					checked,
					searchFilters,
				},
				{
					addToGroupId,
					optRemoveFromGroups,
					removeFromGroupsMode,
					optMoveRandom,
					moveRandomAmount,
					optSetAsTestGroup,
					searchGroups,
				},
			);

			reset(modalBulkAddToGroupState);

			set(pageState, (prev) => ({
				...prev,
				loading: false,
			}));

			notifyBulkAddToGroup(optRemoveFromGroups);
		} catch (error) {
			console.error("### error", error);

			logAppError({
				Component: "RecipientsList",
				Method: "modalBulkAddToGroupConfirmCallback",
				appError: error,
			});

			set<PageStateType>(pageState, (prev) => ({
				...prev,
				status: "error",
				loading: false,
				errorMessage: getIntl().formatMessage({
					id: "Load.Alerts.ErrorGeneric",
				}),
			}));
		}
	};

export const modalBulkAddToGroupAddGroupCallback =
	({ set }: CallbackInterface) =>
	async (searchTerm: string) => {
		try {
			const newItem = await addGroup(searchTerm);

			set(modalBulkAddToGroupState, (prev) => ({
				...prev,
				addToGroupId: newItem.Id,
			}));
		} catch (error) {
			console.error("### error", error);
		}
	};

export const modalBulkAddToGroupChangeCallback =
	({ set }: CallbackInterface) =>
	async (p: Partial<ModalBulkAddToGroupState>) => {
		set<ModalBulkAddToGroupState>(modalBulkAddToGroupState, (prev) => ({
			...prev,
			...p,
		}));
	};

export const modalBulkAddToGroupToggleOptRemoveFromGroupsCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set<ModalBulkAddToGroupState>(modalBulkAddToGroupState, (prev) => ({
			...prev,
			optRemoveFromGroups: !prev.optRemoveFromGroups,
		}));
	};

export const modalBulkAddToGroupToggleOptMoveRandomCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set<ModalBulkAddToGroupState>(modalBulkAddToGroupState, (prev) => ({
			...prev,
			optMoveRandom: !prev.optMoveRandom,
		}));
	};

export const modalBulkAddToGroupToggleOptSetAsTestGroupCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set<ModalBulkAddToGroupState>(modalBulkAddToGroupState, (prev) => ({
			...prev,
			optSetAsTestGroup: !prev.optSetAsTestGroup,
		}));
	};
